import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';
import { Box, Button, Grid, IconButton, makeStyles, Menu, MenuItem, MenuList, MoreVert } from 'housecall-ui';
import Chip from 'housecall-ui/dist/DesignSystem/DataDisplay/Chip/Chip';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import Add from 'housecall-ui/dist/IconsAndImages/Icons/Add';
import ArrowRightAlt from 'housecall-ui/dist/IconsAndImages/Icons/ArrowRightAlt';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import {
  QUESTION_TYPE,
  QuestionType,
  REMINDER_TYPE,
  ReminderType,
  RequestGuideAnswer,
  RequestGuideNode,
} from 'resources/CustomerIntakeFlow/requestGuide';

import { CloneOptionType } from './RequestGuidePage';

export type MenuAction = 'clone';

type RequestGuideChildrenWrapperProps = {
  children?: ReactNode;
  answers: RequestGuideAnswer[];
  currentAnswer: RequestGuideAnswer | undefined;
  isFirst?: boolean;
  isLast?: boolean;
  isDraft?: boolean;
  onAdd: (arg: QuestionType | ReminderType) => void;
  onClone?: (node: RequestGuideNode, options: CloneOptionType) => void;
  data?: RequestGuideNode[];
  isHcpAssistImpersonated: boolean;
  editable: boolean;
};

const useStyles = makeStyles((theme: HCPTheme) => ({
  box: {
    backgroundColor: theme.palette.secondary[100],
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.secondary[400]}`,
    flexGrow: 1,
    position: 'relative',
  },
  draftBox: {
    backgroundColor: 'white',
    borderStyle: 'dashed',
  },
  lineCommon: {
    left: '-34px',
    border: `1px solid ${theme.palette.secondary[400]}`,
    borderRight: 0,
    borderTop: 0,
    borderBottomLeftRadius: theme.spacing(1),
    position: 'absolute',
    width: '34px',
  },
  entryLine: {
    top: '-36px',
    height: '66px',
  },
  line: {
    top: '14px',
    height: 'calc(100% + 42px)',
  },
  draftLine: {
    borderStyle: 'dashed',
  },
}));

export const RequestGuideChildrenWrapper = ({
  children,
  answers,
  currentAnswer,
  isLast,
  isFirst,
  isDraft,
  onAdd,
  onClone,
  data,
  isHcpAssistImpersonated,
  editable,
}: RequestGuideChildrenWrapperProps) => {
  const classes = useStyles();
  const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);

  const handleItemClick = (arg: MenuAction) => () => {
    setMenuAnchor(null);

    const node = data?.length ? data[0] : null;
    if (onClone && node && arg === 'clone') {
      onClone(node, { childNodes: data || [], answer: currentAnswer });
    }
  };

  const addQuestion = () => {
    onAdd(QUESTION_TYPE);
  };

  const addReminder = () => {
    onAdd(REMINDER_TYPE);
  };

  const draftAnswerExist = isLast && answers[answers.length - 1]?.uid !== currentAnswer?.uid;
  const drawDraftLine = isDraft || draftAnswerExist;
  const drawLine = !isLast || (isLast && draftAnswerExist && !isDraft);
  const firstLineClasses = classNames([classes.lineCommon, classes.entryLine], { [classes.draftLine]: isDraft });
  const lineClasses = classNames([classes.lineCommon, classes.line], { [classes.draftLine]: drawDraftLine });
  const boxClasses = classNames(classes.box, { [classes.draftBox]: isDraft });

  return (
    <Box data-testid="requestGuide__children-wrapper" p={2} className={boxClasses}>
      {isFirst && <div className={firstLineClasses} />}
      {drawLine && <div className={lineClasses} />}
      <Box ml={3} mb={1}>
        <Grid container wrap="nowrap" alignItems="center" spacing={2}>
          <Grid item>
            <ArrowRightAlt color="secondary" />
          </Grid>
          <Grid item>
            <Typography noWrap>Show for:</Typography>
          </Grid>
          <Grid item xs={11}>
            {answers.map((answer: RequestGuideAnswer) => {
              const isCurrent = currentAnswer?.uid === answer.uid;
              return (
                <Box display="inline-block" key={answer.uid} ml={1} mb={1}>
                  <Chip
                    label={answer.value}
                    variant={isCurrent ? 'default' : 'outlined'}
                    color={isCurrent ? 'primary' : 'default'}
                  />
                </Box>
              );
            })}
          </Grid>
          {isHcpAssistImpersonated && editable && (
            <Grid item>
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreVert />
              </IconButton>
              <Menu
                elevation={2}
                anchorEl={menuAnchor as Element}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                <MenuList>
                  <MenuItem onClick={handleItemClick('clone')} data-testid="requestGuide__clone">
                    Duplicate
                  </MenuItem>
                </MenuList>
              </Menu>
            </Grid>
          )}
        </Grid>
      </Box>

      {children}

      {editable && (
        <Box mt={1} ml={6}>
          <Grid container spacing={2}>
            <Grid item>
              <Button onClick={addQuestion} color="primary">
                <Add />
                Question
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={addReminder} color="primary">
                <Add />
                Reminder
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};
